import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaPlus, FaTimes, FaGavel } from 'react-icons/fa';
import './ChargeSelector.css';

const ChargeSelector = ({ selectedCharges = [], onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchCharges(searchTerm);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCharges = async (query) => {
    setLoading(true);
    try {
      const response = await axios.get('/charges/search', {
        params: { q: query, limit: 20 }
      });
      if (response.data.success) {
        setSearchResults(response.data.data);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Error searching charges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCharge = (charge) => {
    setSelectedCharge(charge);
    setSearchTerm(`${charge.mca_code} - ${charge.description}`);
    setShowDropdown(false);
  };

  const handleAddCharge = () => {
    if (!selectedCharge) {
      alert('Please select a charge first');
      return;
    }

    if (count < 1) {
      alert('Count must be at least 1');
      return;
    }

    // Check if charge already exists
    const existingIndex = selectedCharges.findIndex(c => c.chargeId === selectedCharge.id);

    if (existingIndex >= 0) {
      // Update existing charge count
      const updated = [...selectedCharges];
      updated[existingIndex] = {
        ...updated[existingIndex],
        count: parseInt(count)
      };
      onChange(updated);
    } else {
      // Add new charge
      const newCharge = {
        chargeId: selectedCharge.id,
        mcaCode: selectedCharge.mca_code,
        description: selectedCharge.description,
        degree: selectedCharge.degree,
        count: parseInt(count)
      };
      onChange([...selectedCharges, newCharge]);
    }

    // Reset form
    setSearchTerm('');
    setSelectedCharge(null);
    setCount(1);
    setSearchResults([]);
  };

  const handleRemoveCharge = (index) => {
    const updated = selectedCharges.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleCountChange = (index, newCount) => {
    if (newCount < 1) return;
    const updated = [...selectedCharges];
    updated[index] = { ...updated[index], count: parseInt(newCount) };
    onChange(updated);
  };

  const getDegreeClass = (degree) => {
    if (!degree) return '';
    const d = degree.toLowerCase();
    if (d.includes('fel')) return 'degree-fel';
    if (d.includes('misd')) return 'degree-misd';
    if (d.includes('civil')) return 'degree-civil';
    return '';
  };

  return (
    <div className="charge-selector">
      <div className="charge-search-section">
        <label>Search and Add Charges</label>
        <div className="charge-search-box" ref={searchRef}>
          <div className="search-input-group">
            <input
              type="text"
              className="charge-search-input"
              placeholder="Search by MCA code or description (e.g., 45-5-102 or DUI)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            />
            <div className="count-input-group">
              <label htmlFor="charge-count">Count:</label>
              <input
                id="charge-count"
                type="number"
                min="1"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="count-input"
              />
            </div>
            <button
              type="button"
              onClick={handleAddCharge}
              className="btn-add-charge"
              disabled={!selectedCharge}
            >
              <FaPlus /> Add
            </button>
          </div>

          {showDropdown && searchResults.length > 0 && (
            <div className="charge-dropdown">
              {loading && <div className="dropdown-loading">Searching...</div>}
              {searchResults.map(charge => (
                <div
                  key={charge.id}
                  className="charge-dropdown-item"
                  onClick={() => handleSelectCharge(charge)}
                >
                  <div className="charge-code">{charge.mca_code}</div>
                  <div className="charge-description">{charge.description}</div>
                  {charge.degree && (
                    <span className={`charge-degree-badge ${getDegreeClass(charge.degree)}`}>
                      {charge.degree}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedCharges.length > 0 && (
        <div className="selected-charges-section">
          <label>
            <FaGavel /> Selected Charges ({selectedCharges.length})
          </label>
          <div className="selected-charges-list">
            {selectedCharges.map((charge, index) => (
              <div key={index} className="selected-charge-item">
                <div className="charge-info">
                  <div className="charge-header">
                    <span className="charge-code-display">{charge.mcaCode}</span>
                    {charge.degree && (
                      <span className={`charge-degree-badge ${getDegreeClass(charge.degree)}`}>
                        {charge.degree}
                      </span>
                    )}
                  </div>
                  <div className="charge-description-display">{charge.description}</div>
                </div>
                <div className="charge-actions">
                  <div className="charge-count-control">
                    <label>Count:</label>
                    <input
                      type="number"
                      min="1"
                      value={charge.count}
                      onChange={(e) => handleCountChange(index, e.target.value)}
                      className="count-input-small"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveCharge(index)}
                    className="btn-remove-charge"
                    title="Remove charge"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargeSelector;
